/**
 * GraphQL Service for handling GraphQL requests with different authentication methods
 * Following the pattern from amira-parent-portal's graphQLService.js
 */
import { Auth } from "aws-amplify";
import { Signer } from "@aws-amplify/core";

/**
 * Helper function to create a delay promise for exponential backoff
 * @param {number} ms - milliseconds to wait
 * @returns {Promise<void>}
 */
const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Calculate exponential backoff delay based on remaining retries
 * @param {number} retries - remaining retry attempts
 * @param {number} baseDelay - base delay in ms (default 1000)
 * @returns {number} delay in milliseconds
 */
const getBackoffDelay = (retries, baseDelay = 1000) => {
  // Exponential backoff: 1s, 2s, 4s for retries 2, 1, 0
  // Clamp attempt to minimum of 0 to handle retries > 3
  const attempt = Math.max(0, 3 - retries);
  return baseDelay * Math.pow(2, attempt);
};

/**
 * GraphQL request with API key authentication
 *
 * @param url - GraphQL endpoint URL
 * @param apiKey - API key for authentication
 * @param query - GraphQL query string
 * @param variables - GraphQL query variables
 * @param retries - Number of retry attempts (default: 0)
 * @returns Promise resolving to GraphQL response
 */
export const graphQLRequest = (
  url,
  apiKey,
  query,
  variables,
  retries = 0
) => {
  const variablesString = JSON.stringify(variables);
  const controller = new AbortController();
  const fetchTimeout = setTimeout(() => {
    controller.abort();
  }, 10000);

  return fetch(url, {
    method: "POST",
    headers: {
      "x-api-key": apiKey,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ query, variables }),
    signal: controller.signal,
  })
    .then((res) => {
      return res.json();
    })
    .then((res) => {
      clearTimeout(fetchTimeout);
      // GraphQL does not throw an error if the request failed. Instead, it populates the res.errors array
      if (!res.errors) {
        return Promise.resolve(res);
      } else {
        if (retries > 0) {
          const delay = getBackoffDelay(retries);
          console.log(`GraphQL error, retrying in ${delay}ms (${retries} retries remaining)`);
          return wait(delay).then(() => graphQLRequest(url, apiKey, query, variables, retries - 1));
        } else {
          return Promise.reject(res.errors[0].message);
        }
      }
    })
    .catch((err) => {
      clearTimeout(fetchTimeout);
      if (retries > 0) {
        const delay = getBackoffDelay(retries);
        console.log(`Network error, retrying in ${delay}ms (${retries} retries remaining)`);
        return wait(delay).then(() => graphQLRequest(url, apiKey, query, variables, retries - 1));
      } else {
        return Promise.reject(err);
      }
    });
};

/**
 * GraphQL request with IAM authentication using AWS SigV4 signing
 * Uses AWS credentials from Cognito Identity Pool via assumeRoleWithWebIdentity
 *
 * @param url - AppSync GraphQL endpoint URL
 * @param query - GraphQL query string
 * @param variables - GraphQL query variables
 * @param retries - Number of retry attempts (default: 3)
 * @returns Promise resolving to GraphQL response
 */
export const graphQLRequestIAM = async (
  url,
  query,
  variables,
  retries = 0
) => {
  // Single linear retry ladder.
  //
  // Retry semantics (do not regress): a response carrying a GraphQL `errors` array — or a missing
  // body — is a failure and is retried; partial data is never accepted as success. A
  // transport/network error, a credential-fetch error, or a signing error is retried the same way.
  // The retry decision lives in exactly ONE place, so an attempt that has exhausted its retries
  // rejects immediately.
  //
  // Why this shape: the previous implementation retried in BOTH a `.then` (on GraphQL errors) and a
  // sibling `.catch`. The `.then` retry path returned a recursive call, whose eventual rejection
  // then propagated into the `.catch`, which retried AGAIN at the same retry count. That double path
  // turned a single logical request into an exponential storm (observed: ~5 login calls -> 47
  // network requests over ~32s when SRS returned a per-row `audioNetworkSignals` field error). A
  // linear loop caps total attempts at retries + 1.
  //
  // Credentials + SigV4 signing run INSIDE the loop, once per attempt: an expired signature (or
  // expired credentials that Amplify can refresh) then self-heals on retry instead of every attempt
  // replaying one stale `x-amz-date`. Keeping credentials, signing, fetch, JSON parse, and the
  // errors/null-body check all under the same per-attempt try/decision means no failure mode
  // (including `res.errors` throwing on a null body) can slip past the ladder or be mislabeled as a
  // credentials error.
  let attemptsRemaining = retries;
  for (;;) {
    // Declared here so the catch below can clear it regardless of where in the attempt we failed;
    // actually created just before the fetch (see below) so the 10s budget covers only the HTTP
    // call. clearTimeout(undefined) is a safe no-op if credentials/signing threw first.
    let fetchTimeout;
    let res;
    try {
      // Get current AWS credentials from Cognito Identity Pool (Amplify caches/refreshes these).
      const credentials = await Auth.currentCredentials();

      // Parse the URL to get required components for signing
      const urlObj = new URL(url);
      const match = url.match(/appsync-api\.([^.]+)\.amazonaws\.com/);
      const region = match ? match[1] : "us-east-2";

      // Prepare the request body
      const body = JSON.stringify({ query, variables });

      // Prepare the request for Signer (uses 'data' in v1.3.3)
      const request = {
        method: "POST",
        url: url,
        data: body,
        headers: {
          "content-type": "application/json",
          host: urlObj.host,
        },
      };

      // Sign with AWS SigV4 — re-signed each attempt so a retry never replays a stale signature.
      const signedRequest = Signer.sign(
        request,
        {
          access_key: credentials.accessKeyId,
          secret_key: credentials.secretAccessKey,
          session_token: credentials.sessionToken,
        },
        {
          region: region,
          service: "appsync",
        }
      );

      // Fresh AbortController + 10s timeout per attempt, started only now that credentials and
      // signing are done — a slow Cognito refresh must not eat into (or pre-abort) the HTTP budget.
      const controller = new AbortController();
      fetchTimeout = setTimeout(() => {
        controller.abort();
      }, 10000);

      const httpRes = await fetch(signedRequest.url, {
        method: signedRequest.method,
        headers: signedRequest.headers,
        body: signedRequest.data || body,
        signal: controller.signal,
      });
      res = await httpRes.json();
    } catch (err) {
      clearTimeout(fetchTimeout);
      // Credential-fetch, signing, transport/abort, or JSON-parse failure. Keep the cause visible
      // in the log — otherwise an auth failure looks identical to a network blip.
      if (attemptsRemaining > 0) {
        const delay = getBackoffDelay(attemptsRemaining);
        console.log(`GraphQL request error (${err && err.name}: ${err && err.message}), retrying in ${delay}ms (${attemptsRemaining} retries remaining)`);
        attemptsRemaining -= 1;
        await wait(delay);
        continue;
      }
      return Promise.reject(err);
    }

    clearTimeout(fetchTimeout);

    // GraphQL populates `errors` instead of throwing; a missing/null body is also a failure. Both
    // are retried, and partial data is never accepted as success.
    if (res && !res.errors) {
      return res;
    }
    if (attemptsRemaining > 0) {
      const delay = getBackoffDelay(attemptsRemaining);
      console.log(`GraphQL error, retrying in ${delay}ms (${attemptsRemaining} retries remaining)`);
      attemptsRemaining -= 1;
      await wait(delay);
      continue;
    }
    // Retries exhausted. Reject with the full response so callers keep access to partial data; but
    // a null/absent body has no such data and would make callers' `err.errors`/`err.data` access
    // throw, so surface a real Error instead.
    return Promise.reject(res || new Error("Empty GraphQL response body"));
  }
};

/**
 * Student Record Service (SRS) request using IAM authentication
 *
 * @param query - GraphQL query string
 * @param variables - GraphQL query variables
 * @returns Promise resolving to GraphQL response
 */
export const studentRecordRequest = (query, variables) => {
  const url = process.env.STUDENT_RECORD_STORE_URL;
  if (!url) {
    return Promise.reject(new Error("STUDENT_RECORD_STORE_URL is not defined"));
  }
  return graphQLRequestIAM(url, query, variables);
};

